import Grid from "@mui/material/Grid";
import {
  ClientAgendaApiData,
  ExpertAgendaResponse,
} from "./type";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import React, { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { agendaContainer } from "../../project-agenda/project-agenda-description/styles";

type Props = {
  expertData: ClientAgendaApiData["experts"];
};

export default function ExpertTab({ expertData }: Props) {
  const [selectedExpert, setSelectedExpert] = useState<ClientAgendaApiData["experts"][0]>(
    expertData[0]
  );
  const [selectedAgendaId, setSelectedAgendaId] = useState<number>(
    expertData[0]?.agenda[0]?.id
  );

  useEffect(() => {
    setSelectedAgendaId(expertData[0]?.agenda[0]?.id);
  }, [selectedExpert]);

  const handleExpertChange = (
    _: React.MouseEvent<HTMLElement>,
    nextExpert: typeof selectedExpert | null
  ) => {
    if (nextExpert) {
      setSelectedExpert(nextExpert);
      setSelectedAgendaId(nextExpert.agenda[0]?.id);
    }
  };

  const handleAgendaChange = (event: SelectChangeEvent<number>) => {
    setSelectedAgendaId(Number(event.target.value));
  };

  const selectedAgenda = selectedExpert?.agenda?.find((a) => a?.id === selectedAgendaId);

  return (
    <Grid container>
      {/* Expert List */}
      <Grid
        item
        xs={12}
        sm={3}
        sx={{
          overflow: "scroll",
          height: { sm: "500px", xs: "initial" },
          maxHeight: { xs: "200px", sm: "500px" },
        }}
      >
        <ToggleButtonGroup
          orientation="vertical"
          value={selectedExpert}
          exclusive
          onChange={handleExpertChange}
          fullWidth
        >
          {expertData.map((expert) => (
            <ToggleButton
              key={expert.id}
              value={expert}
              aria-label={expert.expert_name}
              sx={{ textTransform: "capitalize" }}
            >
              {expert.expert_name}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Grid>

      {/* Agenda Viewer */}
      <Grid
        item
        xs={12}
        sm={9}
        sx={{
          overflow: "scroll",
          border: "1px solid #252B3B75",
          height: "500px",
          p: 2,
        }}
      >
        {selectedAgenda && (
          <>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="agenda-select-label">Select Agenda</InputLabel>
              <Select
                labelId="agenda-select-label"
                value={selectedAgendaId}
                onChange={handleAgendaChange}
                label="Select Agenda"
              >
                {selectedExpert.agenda.map((agenda) => (
                  <MenuItem key={agenda.id} value={agenda.id}>
                    {agenda.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Grid item container sx={agendaContainer}>
              {selectedAgenda.agenda.questions.map((question, index) => {
                const matchingResponse: ExpertAgendaResponse | undefined =
                  selectedAgenda.responses.find(
                    (res) => res.question === question
                  );

                return (
                  <Grid xs={12} item key={index}>
                    <Stack direction="row" spacing={1} mr={4} mt={2}>
                      <Typography
                        fontSize="12px"
                        fontWeight={600}
                        sx={{ color: "#252B3B", opacity: 0.85 }}
                      >
                        Q{index + 1}.
                      </Typography>
                      <Stack flex={1} rowGap={1}>
                        <Typography
                          fontSize="12px"
                          fontWeight={600}
                          sx={{ color: "#484C57" }}
                        >
                          {question}
                        </Typography>

                        {matchingResponse && (
                          <>
                            <Grid ml="10px" container direction="row">
                              <Grid item xs={12} sm={4} md={2}>
                                <Typography
                                  fontSize="12px"
                                  fontWeight={600}
                                  sx={{
                                    color: "#484C57",
                                    opacity: 0.75,
                                  }}
                                >
                                  Response -
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={8} md={10}>
                                <Typography
                                  fontSize="12px"
                                  fontWeight={600}
                                  sx={{
                                    color: "#099A51",
                                    opacity: 0.75,
                                  }}
                                >
                                  {matchingResponse.answer || "—"}
                                </Typography>
                              </Grid>
                            </Grid>

                            {matchingResponse.expert_note && (
                              <Grid ml="10px" container direction="row">
                                <Grid item xs={12} sm={4} md={2}>
                                  <Typography
                                    fontSize="12px"
                                    fontWeight={600}
                                    sx={{
                                      color: "#484C57",
                                      opacity: 0.75,
                                    }}
                                  >
                                    Note -
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={8} md={10}>
                                  <Typography
                                    fontSize="12px"
                                    fontWeight={500}
                                    sx={{
                                      color: "#484C57",
                                      opacity: 0.75,
                                    }}
                                  >
                                    {matchingResponse.expert_note}
                                  </Typography>
                                </Grid>
                              </Grid>
                            )}
                          </>
                        )}
                      </Stack>
                    </Stack>
                    <hr style={{ opacity: 0.75, margin: "0.75rem 0" }} />
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  );
}
